import React, { useContext, useMemo } from "react";
import {
    Box,
    Typography,
    FormGroup,
    FormControl,
    Select,
    MenuItem,
    TextField,
} from "@mui/material";
import { useSelector } from "react-redux";
import { AppContext } from "../../App";
import { selectChartConfig } from "../../selectors/chart";
import { PandFSeriesOptions } from "../chartV2/customSeries/p&FSeries/options";
import ColorSelector from "../colorSelector/colorSelector";
import tinycolor from "tinycolor2";
import CommonChartSettingsTabs from './commonChartSettingsTabs';

type Props = {
    viewKey: string;
};

const PandFSettings = ({ viewKey }: Props) => {
    const appContext = useContext(AppContext)
    const chart = appContext.getChart(viewKey)
    const chartConfig = useSelector(selectChartConfig(viewKey))
    
    const primarySeriesGroupId = useMemo(() => {
        return chart?.primarySeriesGroup?.config?.id
    }, [chart?.primarySeriesGroup?.config?.id])

    const boxMethodSettings = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            return (series as PandFSeriesOptions)?.boxMethod ?? 'atr'
        }
        return 'atr'
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const upColor = useMemo(() => {
        let color = '#26a69a';
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as PandFSeriesOptions)?.upColor ?? '#26a69a'
        }
        return tinycolor(color)
    }, [chartConfig, primarySeriesGroupId])

    const downColor = useMemo(() => {
        let color = '#ef5350';
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as PandFSeriesOptions)?.downColor ?? '#ef5350'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const handleColorChangeSeries = (newColor: string, type: keyof PandFSeriesOptions) => {
        const series = chart?.primarySeriesGroup?.getPrimarySeries()
        if (series && 'updateOptions' in series) {
            series.updateOptions({
                [type]: newColor
            } as any);
        }
    }

    const handleBoxMethodChange = (method: 'atr' | 'traditional' | 'percentage') => {
        const series = chart?.primarySeriesGroup?.getPrimarySeries()
        if (series && 'updateOptions' in series) {
            series.updateOptions({
                boxMethod: method
            } as any)
        }
    }

    const boxSizeValue = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options as PandFSeriesOptions
            if (boxMethodSettings === 'atr') return series.atrLength
            if (boxMethodSettings === 'percentage') return series.percentage
            return series.boxSize
        }
    }, [boxMethodSettings, chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const reversalAmount = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options as PandFSeriesOptions
            return series.reversalAmount ?? 3
        }
        return 3
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const handleBoxSizeValueChange = (value: number) => {
        if (value > 0) {
            const series = chart?.primarySeriesGroup?.getPrimarySeries()
            if (series && 'updateOptions' in series) {
                const key = boxMethodSettings === 'atr' ? 'atrLength' : 
                           boxMethodSettings === 'percentage' ? 'percentage' : 'boxSize'
                series.updateOptions({
                    [key]: value
                } as any)
            }
        }
    }

    const handleReversalAmountChange = (value: number) => {
        if (value > 0) {
            const series = chart?.primarySeriesGroup?.getPrimarySeries()
            if (series && 'updateOptions' in series) {
                series.updateOptions({
                    reversalAmount: value
                } as any)
            }
        }
    }

    const symbolTabContent = (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormGroup>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.1 }}>
                    <Typography variant="body2" sx={{ width: "160px" }}>Up columns (X)</Typography>
                    <ColorSelector 
                        compact 
                        showOnlySwatch 
                        color={upColor.toHexString()} 
                        opacity={upColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'upColor');
                        }} 
                    />
                </Box>
                
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.1 }}>
                    <Typography variant="body2" sx={{ width: "160px" }}>Down columns (O)</Typography>
                    <ColorSelector 
                        compact 
                        showOnlySwatch 
                        color={downColor.toHexString()} 
                        opacity={downColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'downColor');
                        }} 
                    />
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                    <Typography variant="body2" sx={{ width: "160px" }}>Box size method</Typography>
                    <FormControl size="small">
                        <Select
                            sx={{ height: '32px' }}
                            value={boxMethodSettings}
                            onChange={(event) => handleBoxMethodChange(event.target.value as 'atr' | 'traditional' | 'percentage')}
                        >
                            <MenuItem value="atr">ATR</MenuItem>
                            <MenuItem value="traditional">Traditional</MenuItem>
                            <MenuItem value="percentage">Percentage LTP</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                    <Typography variant="body2" sx={{ width: "160px" }}>
                        {boxMethodSettings === 'atr' ? 'ATR Length' : 
                         boxMethodSettings === 'percentage' ? 'Percentage' : 'Box Size'}
                    </Typography>
                    <FormControl size="small">
                        <TextField
                            fullWidth={false}
                            variant="outlined"
                            size="small"
                            value={boxSizeValue}
                            onChange={(event) => handleBoxSizeValueChange(Number(event.target.value))}
                            type="number"
                            sx={{
                                width: '80px',
                                '& .MuiInputBase-root': {
                                    height: '32px'
                                }
                            }}
                        />
                    </FormControl>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                    <Typography variant="body2" sx={{ width: "160px" }}>Reversal Amount</Typography>
                    <FormControl size="small">
                        <TextField
                            fullWidth={false}
                            variant="outlined"
                            size="small"
                            value={reversalAmount}
                            onChange={(event) => handleReversalAmountChange(Number(event.target.value))}
                            type="number"
                            sx={{
                                width: '80px',
                                '& .MuiInputBase-root': {
                                    height: '32px'
                                }
                            }}
                        />
                    </FormControl>
                </Box>
            </FormGroup>
        </Box>
    )

    return <CommonChartSettingsTabs viewKey={viewKey} symbolTabContent={symbolTabContent} />
}

export default PandFSettings;
